/**
 * Schémas Zod — Comparison Notes MVP (Chunk 9.Q.2).
 *
 * Source DB : `app.comparison_notes` (migration 0042).
 *
 * Notes contextuelles broker-driven sur la matrice de comparaison 9.Q.1.
 * 3 niveaux de scope Phase 1 (décision PO) :
 *
 *   project scope :  field_key NULL + submission_id NULL
 *   field scope   :  field_key NOT NULL + submission_id NULL
 *   cell scope    :  field_key NOT NULL + submission_id NOT NULL
 *
 * ADR 0026 axe C Phase 1 — décisions tranchées :
 *   DN-13 = re-confirmation manuelle obligatoire (note JAMAIS réutilisée
 *           automatiquement dans une analyse future)
 *   DN-14 = enum strict scope (3 niveaux via nullable field_key + submission_id)
 *           + body texte libre 1..4000 ; pas de tags Phase 1
 *
 * Scope auth Phase 1 — strictement humain-only :
 *   - GET list : tout `authenticated` (RLS filtre tenant + project scope check
 *     handler-side).
 *   - POST/PATCH/DELETE : JWT user only (api_key rejetée handler-side via
 *     early check `auth.kind === "user"` → 403 COMPARISON_NOTES_FORBIDDEN_USER_ONLY).
 *
 * Anti-leak strict (wire shape `ComparisonNoteRow`) :
 *   EXCLUS : `tenant_id` (implicite via RLS), `created_by_user` UUID brut,
 *            `created_by_agent` UUID brut, `deleted_at` (interne).
 *   EXPOSÉS : display_name jointures SAFE pour author humain + agent (Phase 2).
 */

import { z } from "zod";

import { UuidSchema } from "./common.js";

// -----------------------------------------------------------------------------
// Row (lecture wire-side) — GET /v1/projects/:id/comparison-notes
// -----------------------------------------------------------------------------
//
// Anti-leak strict : aucun UUID brut d'author (parité 9.M.7 forensic UI badges
// qui exposent uniquement `*_by_agent_display_name`). Le tenant_id reste
// implicite via RLS. Le deleted_at est interne (soft-delete filtered handler-side).

export const ComparisonNoteRowSchema = z.object({
  id: UuidSchema,
  project_id: UuidSchema,
  // NULL = scope projet. Non-NULL = scope field (submission_id NULL) ou
  // cell (submission_id NOT NULL).
  field_key: z.string().nullable(),
  // NULL = scope projet ou field. Non-NULL = scope cell.
  submission_id: UuidSchema.nullable(),
  body: z.string(),
  // Display names joints (SAFE, parité 9.M.5/9.M.7 *_by_agent_display_name pattern).
  // Phase 1 humain-only : agent_display_name reste null en pratique.
  created_by_user_display_name: z.string().nullable(),
  created_by_agent_display_name: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ComparisonNoteRow = z.infer<typeof ComparisonNoteRowSchema>;

// -----------------------------------------------------------------------------
// Create (POST /v1/comparison-notes) — JWT user only Phase 1
// -----------------------------------------------------------------------------
//
// Validation Zod-side :
//   - project_id : UUID requis (handler valide ∈ tenant)
//   - field_key  : optionnel/nullable, regex canonique parité 9.Q.1 si fourni
//   - submission_id : optionnel/nullable UUID (handler valide ∈ project)
//   - body : 1..4000 chars
//
// Le scope check (field_key NULL + submission_id NOT NULL → invalide) est levé
// côté handler via `bilingualHttpError("COMPARISON_NOTES_SCOPE_INVALID", 422)`
// AVANT INSERT (défense en profondeur + message clair vs CHECK PG générique).
// La CHECK DB `comparison_notes_scope_chk` est garde-fou DB-side.

export const CreateComparisonNoteSchema = z.object({
  project_id: UuidSchema,
  field_key: z
    .string()
    .regex(/^[a-z][a-z0-9_.]*$/, "field_key must match canonical format")
    .nullable()
    .optional(),
  submission_id: UuidSchema.nullable().optional(),
  body: z.string().min(1).max(4000),
});
export type CreateComparisonNoteInput = z.infer<typeof CreateComparisonNoteSchema>;

// -----------------------------------------------------------------------------
// Update (PATCH /v1/comparison-notes/:id) — JWT user only Phase 1
// -----------------------------------------------------------------------------
//
// Phase 1 mutable : seul `body` est modifiable. Scope (field_key + submission_id)
// est immutable — pour changer le scope, supprimer + recréer (préserve audit log
// forensic clair vs editer le scope qui rend l'historique trompeur).

export const UpdateComparisonNoteSchema = z.object({
  body: z.string().min(1).max(4000),
});
export type UpdateComparisonNoteInput = z.infer<typeof UpdateComparisonNoteSchema>;

// -----------------------------------------------------------------------------
// List query — GET /v1/projects/:id/comparison-notes
// -----------------------------------------------------------------------------
//
// Filtres optionnels (cardinalité faible Phase 1, pas de pagination — assumée
// bornée par projet < 50 notes ; dette P3 si volumétrie dépasse) :
//   - field_key : filter sur un field_key précis (regex canonique)
//   - submission_id : filter sur cell-scope d'une submission précise
//
// Tous les niveaux de scope sont retournés par défaut (project + field + cell)
// pour qu'un broker voit l'ensemble des notes du projet en un seul GET.

export const ComparisonNotesListQuerySchema = z.object({
  field_key: z
    .string()
    .regex(/^[a-z][a-z0-9_.]*$/, "field_key must match canonical format")
    .optional(),
  submission_id: UuidSchema.optional(),
});
export type ComparisonNotesListQuery = z.infer<typeof ComparisonNotesListQuerySchema>;
